import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserService } from '@shared/services/user/user.service';
import { HttpClient } from "@angular/common/http";
import { forkJoin, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { catchError, map } from 'rxjs/operators';
import { PlannerProjectService } from "@modules/planner/services/planner-project/planner-project.service";
import { WorkOrderService } from "@modules/planner/services/workorder/work-order.service";
import { WorkOrder, WorkOrderSearch, WorkOrderSearchTypes } from '@shared/models/work-order';
import { StatusDefinitions } from '@shared/models/status';
import { CustomerIds } from "@shared/models/customer-settings";
import { MultiSelectSearchModes, MultiSelectSearchTypes } from "@shared/models/multi-select-search";
import { POWERLINE_GROUP_HIGHLIGHT_COLOR, POWERLINE_GROUP_POLYLINE_SHAPE_OPTIONS } from "@shared/models/powerlines";
import { TranslateService } from "@ngx-translate/core";
import { MapUtils } from "@shared/services/mapUtils/map-utils.service";
import { DateUtils } from "@shared/services/dateUtils/date-utils.service";
import { RoutePlanningService } from "@modules/planner/services/route-planning/route-planning.service";
import { BaseReport, RouteNote, RoutePlanningBase, RoutePlanningRoute } from "@shared/models/route-planning";
import { ToastService } from "@shared/services/toast/toast.service";
import { BasemapService } from '@shared/services/basemap/basemap.service';
import { EditableReport } from '@modules/planner/components/edit-report-dialog/edit-report-dialog.component';

declare const L: any;
enum MapLayerFilter {
    ALL = 'all',
    FRAMES = 'frames',
    INVERTERS = 'inverters',
}

@Component({
    selector: 'app-project-dashboard',
    templateUrl: './project-dashboard.component.html',
    styleUrls: ['./project-dashboard.component.scss'],
    providers: [HttpClient],
    standalone: false
})
export class ProjectDashboardComponent implements OnInit, OnDestroy {

    componentDestroyed$: Subject<boolean> = new Subject()
    allCalendars;
    teams = Array()
    projectId: any
    allStates: any = { "states": [] }
    pageLoading = true

    monDate: any
    tueDate: any
    wedDate: any
    thuDate: any
    friDate: any
    satDate: any
    sunDate: any
    todaysDate: any
    chosenDate: any
    today: any

    calendarsLoading = true
    weekdays: any
    calendarObjectIsSet = false
    thisDayHover = -1
    hoveredCalendar = -1
    customCalendarObject = {}
    showCalendarsWithSlots = false

    msas: any
    userid: any
    projectData: any
    projectName: any
    msaEstimatesLoading = true

    //map related stuff
    workOrders: WorkOrder[] = [];
    map: any
    workOrderMarkers: Map<number, L.Marker> = new Map();
    showColors = false
    interruptedColor = "#ff1122";

    selectedMarker: any = { 'identifier': null, 'states': [], 'imagecount': 0 }
    selectedImage: any
    showSidebar: boolean = false
    isLoading: boolean = false
    selectedState: any


    customerId = 0;
    customerIds = CustomerIds;
    inverters: WorkOrder[] = []
    inverterMarkers: Map<number, L.Marker> = new Map();
    canvas = L.canvas()
    type: number = 0
    type3Workorders: WorkOrder[] = []
    type3WorkorderMarkers: Map<number, L.Marker> = new Map();
    rowNumbers: any
    rowNumberMarkers: any
    powerLineWorkOrders: WorkOrder[] = []
    powerLineWorkOrderMarkers: Map<number, L.Marker> = new Map();
    powerLineGroupMarkers: Map<string, { polyline: L.Polyline, workOrders: WorkOrder[] }> = new Map();
    showPowerLineGroupsInput = false;
    powerLineGroupsVisible = false;
    selectedPowerLineGroup: WorkOrder[] = [];
    selectedPowerLineGroupHighlight!: L.Polyline;
    powerlineHighlightMarkers: Map<number, L.Marker> = new Map();
    baseMarkers: Map<number, L.Marker> = new Map();
    selectedBase!: RoutePlanningBase;
    selectedBaseReports: BaseReport[] = [];
    showBaseSidebar = false;
    selectedRoute!: RoutePlanningRoute;
    showRouteSidebar = false;
    routeNoteMarkers: Map<number, L.Marker> = new Map();
    selectedRouteNote!: RouteNote;
    showRouteNoteSidebar = false;
    highlightedMarkers: { id: number, color: string, weight: number }[] = [];
    routeTooltip = L.tooltip({ permanent: false, direction: 'bottom', offset: L.point(0, 10) });
    selectedStatuses: any[] = [];
    editedReport: EditableReport | null = null;
    editedReportPhase: string | null = null;
    reports: Report[] = [];
    multiSelectSearchModes = MultiSelectSearchModes;
    private searchPointer!: L.Marker | null;
    statusStateDefinitionMap = new Map<number, string>();

    layerFilter: MapLayerFilter = MapLayerFilter.ALL;

    hasInverters: boolean = false;
    hasPowerlines: boolean = false

    private currentFilteredWorkOrders: { type: number, workOrders: WorkOrder[] } | null = null;


    constructor(
        private userService: UserService,
        private plannerProjectService: PlannerProjectService,
        private workOrderService: WorkOrderService,
        private translateService: TranslateService,
        private routePlanningService: RoutePlanningService,
        private toastService: ToastService,
        private basemapService: BasemapService,
    ) { }

    ngOnInit() {
        this.setUpCustomRectangleCanvas();
        this.setUpCustomTextMarkerCanvas();
        this.todaysDate = new Date();
        this.today = this.todaysDate.getDay();

        // Get the current project ID first
        this.getCurrentProjectId(() => {
            // Use switchMap to first get row numbers, then get project data
            this.workOrderService.getRowNumbers(this.projectId)
                .pipe(
                    switchMap(rowNumbers => {
                        this.rowNumbers = rowNumbers; // Store the row numbers

                        // After getting row numbers, fetch project data
                        return this.plannerProjectService.getProjectById(this.projectId);
                    }),
                    takeUntil(this.componentDestroyed$), // Ensure this happens after fetching project by ID
                    switchMap(projectData => {
                        this.projectData = projectData;
                        this.projectName = projectData.name;

                        for (let statusObj of Object.values(projectData.statuses)) {
                            const status = statusObj as any;
                            const definition = JSON.parse(status.definition);
                            this.statusStateDefinitionMap.set(status.state, definition['en']);
                        }


                        // Fetch project statuses in parallel
                        return forkJoin({
                            status1: this.plannerProjectService.getProjectStatus(this.projectId, 1),
                            status2: this.plannerProjectService.getProjectStatus(this.projectId, 2),
                            status3: this.plannerProjectService.getProjectStatus(this.projectId, 3),
                            status4: this.plannerProjectService.getProjectStatus(this.projectId, 4),
                        });
                    })
                )
                .subscribe({
                    next: statusData => {

                        statusData.status1.states.forEach(s => s.type = 1);
                        statusData.status2.states.forEach(s => s.type = 2);
                        statusData.status3.states.forEach(s => s.type = 3);
                        statusData.status4.states.forEach(s => s.type = 4);



                        // Combine status data into a single object
                        this.allStates = {
                            ...statusData.status1,
                            powerlineStates: statusData.status4.states,
                            states: [
                                ...statusData.status1.states,
                                ...statusData.status2.states,
                                ...statusData.status3.states,
                                ...statusData.status4.states,
                            ]
                                .map(state => ({ ...state, show: false }))
                                .sort((a, b) => {
                                    // First sort by type
                                    if (a.type !== b.type) {
                                        return a.type - b.type;
                                    }
                                    // Then sort by state
                                    return a.state - b.state;
                                })
                        };

                        for (let status of this.allStates.states) {
                            const definitionEn = this.statusStateDefinitionMap.get(status.state);
                            if (definitionEn && MapUtils.statusCompare(definitionEn, StatusDefinitions.NOT_STARTED)) {
                                status.hidden = true;
                            }
                        }

                        for (let status of this.allStates.powerlineStates) {
                            const definitionEn = this.statusStateDefinitionMap.get(status.state);
                            if (definitionEn && MapUtils.statusCompare(definitionEn, StatusDefinitions.NOT_STARTED)) {
                                status.hidden = true;
                            }
                        }

                        this.pageLoading = false;

                        // Fetch work orders and inverters simultaneously
                        forkJoin({
                            workorders: this.getWorkOrders(null, 1),
                            inverters: this.getWorkOrders(null, 2),
                            type3Workorders: this.getWorkOrders(null, 3),
                            type4Workorders: this.getWorkOrders(null, 4),
                        }).pipe(takeUntil(this.componentDestroyed$)).subscribe({
                            next: results => {
                                this.workOrders = results.workorders;
                                this.inverters = results.inverters;
                                this.hasInverters = Array.isArray(this.inverters) && this.inverters.length > 0;
                                this.type3Workorders = results.type3Workorders;
                                this.powerLineWorkOrders = results.type4Workorders;
                                this.hasPowerlines = Array.isArray(this.powerLineWorkOrders) && this.powerLineWorkOrders.length > 0
                                this.initMap();
                            },
                            error: error => {
                                console.error('Error fetching inverters or work orders:', error);
                            }
                        });
                    },
                    error: error => {
                        console.error('Error fetching project or status:', error);
                    }
                });
        });
    }

    private drawBases(projectId: number): void {
        this.routePlanningService.getBases(projectId)
            .subscribe(bases => {
                bases.forEach(base => {
                    const marker = MapUtils.createBaseIcon(base, this.baseMarkers, this.map, this.translateService, true, false);
                    marker.on('click', () => this.showBaseDetails(base));
                });
            });
    }

    private drawRoutes(projectId: number): void {
        this.routePlanningService.getRoutes(projectId)
            .subscribe(routes => {
                routes.forEach(route => {
                    const polylineLayer = MapUtils.createRoutePolyline(this.map, route);
                    if (!polylineLayer) return;

                    const title = route.identifier
                        ? route.identifier
                        : this.translateService.instant('planner.routePlanning.route') + ' #' + route.id;

                    MapUtils.setPolylineTooltip(this.map, this.routeTooltip, polylineLayer, `
            <div class="title">${title}</div>
            ${route.notes ? '<div class="notes">' + route.notes + '</div>' : ''}
          `);

                    polylineLayer.on('click', () => this.showRouteDetails(route));
                });
            });
    }

    private showRouteDetails(route: RoutePlanningRoute): void {
        this.selectedRoute = route;
        this.showRouteSidebar = true;
        this.showBaseSidebar = false;
        this.showRouteNoteSidebar = false;
        this.showSidebar = false;
    }

    private drawRouteNotes(projectId: number): void {
        this.routeNoteMarkers.forEach(marker => marker.remove());
        this.routeNoteMarkers.clear();

        this.routePlanningService.getRouteNotes(projectId)
            .subscribe(routeNotes => {
                routeNotes.forEach(routeNote => {
                    const marker = MapUtils.createRouteNoteIcon(routeNote);
                    this.routeNoteMarkers.set(routeNote.id, marker);

                    marker.bindTooltip(routeNote.notes, {
                        direction: 'top',
                        offset: [0, -10],
                        className: 'route-note-tooltip'
                    });

                    marker.addTo(this.map);

                    marker.on('click', () => this.showRouteNoteDetails(routeNote));
                });
            });
    }

    private showRouteNoteDetails(routeNote: RouteNote): void {
        this.selectedRouteNote = routeNote;
        this.showRouteNoteSidebar = true;
        this.showRouteSidebar = false;
        this.showBaseSidebar = false;
        this.showSidebar = false;
    }

    getSortedStates(): any[] {
        const states = this.customerId === this.customerIds.POWERLINES
            ? this.allStates.powerlineStates
            : this.allStates.states;

        return states.slice().sort((a, b) => {
            // First sort by type
            if (a.type !== b.type) {
                return a.type - b.type;
            }
            // Then sort by state
            return a.state - b.state;
        });
    }

    private showBaseDetails(base: RoutePlanningBase): void {
        this.routePlanningService.getReportsForBase(base.id).subscribe({
            next: reports => {
                this.selectedBase = base;
                this.selectedBaseReports = reports.sort((a, b) => {
                    if (!a.attend_time || !b.attend_time) return 0;
                    return new Date(b.attend_time).getTime() - new Date(a.attend_time).getTime();
                });

                this.showBaseSidebar = true;
                this.showSidebar = false;

                for (let report of this.selectedBaseReports) {
                    if (!report?.images) continue;
                    for (let image of report.images) {
                        if (!image.id) continue;
                        this.workOrderService.fetchImage(image.id).subscribe(imageUrl => {
                            image.url = imageUrl;
                        });
                    }
                }
            },
            error: error => {
                this.toastService.sendToast(false, this.translateService.instant('planner.projectDashboard.reportsNotFound'));
            }
        });
    }

    drawRowNumbersOnMap(): void {
        // Remove any existing row number markers from the map
        if (this.rowNumberMarkers) {
            this.rowNumberMarkers.forEach(marker => marker.remove());
        }
        this.rowNumberMarkers = new Map<number, L.Layer>(); // Reset the row number markers array

        const canvasRenderer = L.canvas(); // Create a canvas renderer

        this.rowNumbers.forEach((rowNumber) => {
            const lat = Number(rowNumber.position_x);
            const lng = Number(rowNumber.position_y);

            if (!isNaN(lat) && !isNaN(lng)) {
                // Create a custom text marker
                const marker = L.customTextMarker([lat, lng], {
                    renderer: canvasRenderer,
                    text: rowNumber.number.toString(),
                    fontSize: 11, // Adjust font size as needed
                    fontColor: 'black' // Adjust font color as needed
                }).addTo(this.map);

                // Add the marker to the rowNumberMarkers array
                this.rowNumberMarkers.set(rowNumber.id, marker);
            } else {
                console.error(`Invalid coordinates for row number id=${rowNumber.id}: lat=${lat}, lng=${lng}`);
            }
        });
    }

    getInverterSize(zoomLevel: number): [number, number] {
        switch (zoomLevel) {
            case 19:
                return [35, 35]
            case 18:
                return [15, 15];
            case 17:
                return [7, 7];
            case 16:
                return [1.5, 1.5];
            case 15:
                return [0.5, 0.5];
            default:
                return [60, 60];
        }
    }


    getWorkOrders(limit: number | null, type: number): Observable<WorkOrder[]> {
        return this.workOrderService.getWorkOrders(limit, type);
    }

    /**
     * Gets current project id and calls a callback method to tell that it has finished
     * @param cb
     */
    getCurrentProjectId(cb) {
        this.userService.getUserInfo().subscribe(
            data => {
                this.projectId = data.current_project
                this.userid = data.id
                this.customerId = data.customer_id;
                // cb() = callback to let the other function know that this one is finished
                cb()
            }
        );
    }

    initMap() {
        const centerCoordinates = this.getCenterCoordinates();


        if (this.map) {
            this.applyLayerFilter(); // redraws according to current filter
            return;
        }

        if (!document.getElementById('mapProject')) return;

        this.map = L.map('mapProject', { editable: true });

        this.map.zoomControl.setPosition('topright');

        this.basemapService.attach(this.map, { customerId: this.customerId, minZoom: 3, maxZoom: 25 });

        this.map.on('locationerror', () => {
            this.map.panTo({ lat: centerCoordinates.latitude, lon: centerCoordinates.longitude });
        });

        // Set the initial center of the map
        const initialCenter = L.latLng(centerCoordinates.latitude, centerCoordinates.longitude);
        let mapZoom = 15
        if (this.customerId === 1) mapZoom = 16
        else if (this.customerId === CustomerIds.POWERLINES) mapZoom = 11
        else if (this.customerId === 4) mapZoom = 13
        this.map.setView(initialCenter, mapZoom);

        this.drawRowNumbersOnMap();

        this.drawWorkOrders();
        this.drawInverters();
        this.drawType3Workorders();
        if (this.customerId === this.customerIds.POWERLINES) {
            this.drawPowerLineWorkOrders();
            this.drawBases(this.projectId);
            this.drawRoutes(this.projectId);
            this.drawRouteNotes(this.projectId);
        }

        this.addZoomListener();
        this.addClickListener();
        setTimeout(() => {
            this.allStates = { ...this.allStates }; // trigger change detection
        }, 0);
    }

    get selectedTypeId(): MultiSelectSearchTypes {
        if (this.projectData?.customer_id === this.customerIds.POWERLINES) {
            return MultiSelectSearchTypes.POWERLINE;
        }
        return MultiSelectSearchTypes.INVERTER;
    }

    onSearchChange(workOrders: WorkOrderSearch[]): void {
        this.resetMarkerHighlights();
        for (const workOrder of workOrders) {
            switch (workOrder.search_type) {
                case WorkOrderSearchTypes.STANDARD:
                    this.highlightMarker(workOrder.id);
                    break;

                case WorkOrderSearchTypes.INVERTER:
                    this.highlightMarker(workOrder.id);
                    break;

                case WorkOrderSearchTypes.TYPE3:
                    this.highlightMarker(workOrder.id);
                    break;

                case WorkOrderSearchTypes.POWERLINE:
                    this.setPowerlineMarkerHighlight(workOrder, true);
                    break;
            }
        }
    }

    onSearchClick(workOrder: WorkOrderSearch): void {
        if (!workOrder?.latitude || !workOrder.longitude) return;

        this.resetMarkerHighlights();
        this.highlightMarker(workOrder.id);
        this.goToLocation(Number(workOrder.latitude), Number(workOrder.longitude));
        this.showDetails(workOrder.id);

        this.searchPointer?.remove();
        this.searchPointer = MapUtils.createPointerMarker(workOrder.latitude, workOrder.longitude);
        this.searchPointer?.addTo(this.map);
    }

    onSearchLocationClick(workOrder: WorkOrderSearch): void {
        if (!workOrder?.latitude || !workOrder.longitude) return;

        this.resetMarkerHighlights();
        this.highlightMarker(workOrder.id);
        this.goToLocation(Number(workOrder.latitude), Number(workOrder.longitude));
    }

    private resetMarkerHighlights(): void {
        this.searchPointer?.remove();
        this.powerlineHighlightMarkers.forEach(marker => marker.remove());
        this.powerlineHighlightMarkers.clear();

        for (const markerOptions of this.highlightedMarkers) {
            const marker = this.workOrderMarkers.get(markerOptions.id);
            (marker as any).setStyle({
                color: markerOptions.color,
                weight: markerOptions.weight,
            });
        }

        this.highlightedMarkers = [];
    }

    private highlightMarker(workOrderId: number): void {
        const marker = this.workOrderMarkers.get(workOrderId);
        if (!marker) return;

        const options = marker.options as any;
        this.highlightedMarkers.push({
            id: workOrderId,
            color: options.color,
            weight: options.weight,
        });

        (marker as any).setStyle({
            color: 'green',
            weight: 2,
        });
    }

    private goToLocation(lat: number, lng: number, zoom: number = 17): void {
        if (this.map) {
            this.map.flyTo([lat, lng], zoom, {
                animate: true,
                duration: 1
            });
        }
    }

    drawInverters(showOnlyIds?: number[]) {
        if (this.inverterMarkers) {
            this.inverterMarkers.forEach(marker => marker.remove());
        }
        this.inverterMarkers = new Map();

        if (!this.map.getPane('inverterPane')) {
            this.map.createPane('inverterPane')
            this.map.getPane('inverterPane').style.zIndex = 20
        }

        const currentZoomLevel = this.map.getZoom(); // Get current zoom level


        for (let inverter of this.inverters) {
            let size = MapUtils.getRelativeMarkerSize(inverter.frame_size, currentZoomLevel, this.customerId)
            if (Array.isArray(showOnlyIds) && !showOnlyIds.includes(inverter.id)) continue;

            let color = 'black';
            let border = 'black'
            let opacity = 1;

            if (inverter.interrupted) {
                color = this.interruptedColor;
                border = this.interruptedColor
            } else {
                for (let i = 0; i < this.allStates.states.length; i++) {
                    const element = this.allStates.states[i];
                    if (element.id == inverter.status_id) {
                        color = element.color
                        border = element.color
                        break;
                    }
                }
            }
            if (inverter.deviation) {
                border = 'red';
            }

            const lat = Number(inverter.latitude);
            const lng = Number(inverter.longitude);

            if (!isNaN(lat) && !isNaN(lng)) {
                // NEW: circle marker for inverters
                const scale = (this.customerId === 2) ? 2 : 1; // tweak factor as needed
                const radius = (Math.max(size[0], size[1]) / 5) * scale;
                const marker = L.circleMarker([lat, lng], {
                    pane: 'inverterPane',
                    renderer: this.canvas,
                    radius: radius,
                    fillColor: color,
                    color: border,
                    opacity: opacity,
                    fillOpacity: opacity,
                    weight: 1,
                    isSelected: false,
                    frame_size: inverter.frame_size
                } as any).addTo(this.map);

                (marker as any).options['size'] = size;

                marker.on('click', () => this.showDetails(inverter.id));

                // Add the marker to the inverterMarkers array
                this.inverterMarkers.set(inverter.id, marker);
            } else {
                console.error(`Invalid coordinates for inverter id=${inverter.id}: lat=${lat}, lng=${lng}`);
            }
        }
    }

    updateMarkerStyle(marker, color, opacity, isSelected) {
        if (isSelected) {
            // Update the marker to be white with a black outline
            marker.setStyle({
                fillColor: 'white',
                color: 'black',
                fillOpacity: opacity,
                weight: 2 // Adjust the outline thickness as needed
            });
        } else {
            // Revert to the original style
            marker.setStyle({
                fillColor: color,
                color: color,
                fillOpacity: opacity,
                weight: 1 // Adjust the outline thickness as needed
            });
        }

        // Update the isSelected property
        marker.options.isSelected = isSelected;
    }

    drawPowerLineWorkOrders(showOnlyIds?: number[]): void {
        if (this.powerLineWorkOrderMarkers) {
            this.powerLineWorkOrderMarkers.forEach(marker => marker.remove());
        }

        this.powerLineWorkOrderMarkers.clear();
        const workorders: WorkOrder[] = [];
        this.workOrderService.getReportsProject(this.projectId).subscribe(reports => {
            this.reports = reports;
            for (let workOrder of this.powerLineWorkOrders) {
                if (Array.isArray(showOnlyIds) && !showOnlyIds.includes(workOrder.id)) continue;
                workorders.push(workOrder);
            }

            this.createPowerLineMarker(workorders, reports);
        });
    }

    private createPowerLineMarker(workOrders: WorkOrder[], reports): void {
        workOrders.forEach(workOrder => {
            const markerOld = this.powerLineWorkOrderMarkers.get(workOrder.id);
            if (markerOld) markerOld.remove();

            let color = (workOrder.priority_status ? workOrder.priority_status.color : workOrder.color) ?? '#000';

            // interrupted
            if (workOrder.interrupted) {
                color = workOrder.color;
            }

            const marker = MapUtils.getPowerlineCustomMarker(workOrder, color, 30, this.map);
            marker.on('click', () => this.showDetails(workOrder.id));


            let workorderReports = reports.filter(report => report.workorder_id === workOrder.id)


            workOrder.reportsAmount = 0
            const statusesHtml = this.allStates.states.map(status => {

                let color = 'gray'
                if (workorderReports.length > 0) {
                    let report = workorderReports.find(report => report.state == status.state)
                    if (report) {
                        if (report.report_type === 1 || report.report_type === 4) color = status.color
                        else if (report.report_type === 3 || report.report_type === 2) color = 'red'
                        workOrder.reportsAmount++
                    }
                }
                return `<span style="
                    display: inline-block;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background-color: ${color};
                    margin: 0 2px;
                "></span>`
            }).join('');


            marker.bindTooltip(`<div style="text-align: center;">
                    <div>${workOrder.identifier}</div>
                    <div class="d-flex">
                        <div class="d-flex align-self-center">${workOrder.reportsAmount}/${this.allStates.states.length}</div>
                        <div>${statusesHtml}</div>
                    </div>
                </div>`, {
                direction: 'top',
                offset: [0, -10]
            });

            this.powerLineWorkOrderMarkers.set(workOrder.id, marker);
        });
    }

    drawWorkOrders(showOnlyIds?: number[]) {

        // Remove any existing work order markers from the map
        if (this.workOrderMarkers) {
            this.workOrderMarkers.forEach(marker => marker.remove());
        }
        this.workOrderMarkers = new Map(); // Reset the work order markers array

        if (!this.map.getPane('workorderPane')) {
            this.map.createPane('workorderPane')
            this.map.getPane('workorderPane').style.zIndex = 10
        }

        const currentZoomLevel = this.map.getZoom()


        for (let workorder of this.workOrders) {
            let size = MapUtils.getRelativeMarkerSize(workorder.frame_size, currentZoomLevel, this.customerId)
            if (Array.isArray(showOnlyIds) && !showOnlyIds.includes(workorder.id)) continue;

            let color = 'gray';
            let border = 'gray';
            if (typeof workorder.status_id === 'number') {

                if (workorder.interrupted) {
                    color = this.interruptedColor;
                    border = this.interruptedColor
                } else {
                    for (let i = 0; i < this.allStates.states.length; i++) {
                        const element = this.allStates.states[i];
                        if (element.id == workorder.status_id) {
                            color = element.color
                            border = element.color
                            break;
                        }
                    }
                }
                if (workorder.deviation) {
                    border = 'red';
                }

                const opacity = 1;
                const lat = Number(workorder.latitude);
                const lng = Number(workorder.longitude);

                if (!isNaN(lat) && !isNaN(lng)) {
                    const marker = L.customRectangleMarker([lat, lng], {
                        pane: 'workorderPane',
                        renderer: this.canvas,
                        size: size, // Size for rectangle
                        fillColor: color,
                        color: border,
                        opacity: opacity,
                        fillOpacity: opacity,
                        weight: 1, // Ensure the border weight is specified
                        isSelected: false, // Initialize as not selected
                        frame_size: workorder.frame_size
                    }).addTo(this.map);

                    // Click event: show details
                    marker.on('click', () => this.showDetails(workorder.id));

                    // Add the marker to the workOrderMarkers map
                    this.workOrderMarkers.set(workorder.id, marker);
                } else {
                    console.error(`Invalid coordinates for workorder id=${workorder.id}: lat=${lat}, lng=${lng}`);
                }
            } else {
                console.error("Error, the workorder does not have a state")
            }
        }
    }

    drawType3Workorders(showOnlyIds?: number[]) {
        // Remove any existing work order markers from the map
        if (this.type3WorkorderMarkers) {
            this.type3WorkorderMarkers.forEach(marker => marker.remove());
        }
        this.type3WorkorderMarkers = new Map(); // Reset the work order markers array


        if (!this.map.getPane('workorderPane2')) {
            this.map.createPane('workorderPane2')
            this.map.getPane('workorderPane2').style.zIndex = 10
        }

        const zoom = this.map.getZoom()
        for (let workorder of this.type3Workorders) {
            if (Array.isArray(showOnlyIds) && !showOnlyIds.includes(workorder.id)) continue;

            let color = 'green';
            let border = 'green';
            if (typeof workorder.status_id === 'number') {

                if (workorder.interrupted) {
                    color = this.interruptedColor;
                    border = this.interruptedColor
                } else {
                    for (let i = 0; i < this.allStates.states.length; i++) {
                        const element = this.allStates.states[i];
                        if (element.id == workorder.status_id) {
                            color = element.color
                            border = element.color
                            break;
                        }
                    }
                }
                if (workorder.deviation) {
                    border = 'red';
                }

                if (workorder.comment !== 0) {
                    border = 'black'
                }

                const opacity = 1;
                const lat = Number(workorder.latitude);
                const lng = Number(workorder.longitude);

                if (!isNaN(lat) && !isNaN(lng)) {
                    if (this.customerId === CustomerIds.POWERLINES) {
                        // Use SVG icon
                        const icon = L.icon({
                            iconUrl: this.getPowerlineIconUrl(workorder.id),
                            iconSize: [30, 30],
                            iconAnchor: [15, 15]
                        });
                        const marker = L.marker([lat, lng], { icon: icon }).addTo(this.map);
                        // Show identifier on hover
                        marker.bindTooltip(workorder.identifier, {
                            permanent: false,
                            direction: 'top',
                            className: 'powerline-tooltip'
                        });
                        marker.on('click', () => this.showDetails(workorder.id));
                        this.type3WorkorderMarkers.set(workorder.id, marker);
                        this.mockDrawHomeIcon();
                    } else {
                        // Use rectangle marker
                        const size = MapUtils.getRelativeMarkerSize(workorder.frame_size, zoom, this.customerId)
                        const marker = L.customRectangleMarker([lat, lng], {
                            pane: 'workorderPane2',
                            renderer: this.canvas,
                            size: size,
                            fillColor: 'green',
                            color: 'green',
                            opacity: 1,
                            fillOpacity: 1,
                            weight: 1,
                            isSelected: false,
                            frame_size: workorder.frame_size
                        }).addTo(this.map);

                        marker.on('click', () => this.showDetails(workorder.id));
                        this.type3WorkorderMarkers.set(workorder.id, marker);
                    }
                } else {
                    console.error(`Invalid coordinates for workorder id=${workorder.id}: lat=${lat}, lng=${lng}`);
                }
            } else {
                console.error("Error, the workorder does not have a state")
            }
        }



    }

    mockDrawHomeIcon() {
        const homeIcon = L.icon({
            iconUrl: '/assets/icons/powerlines/Home.png',
            iconSize: [25, 25],
            iconAnchor: [20, 20]
        });

        L.marker([60.64223081373168, 25.26548413017419], { icon: homeIcon }).addTo(this.map);
    }


    // Utility function to get icon URL based on randomized poletype and kiristetty
    getPowerlineIconUrl(workorderId: number): string {
        const poletype = workorderId % 2 === 0 ? 1 : 2; // Even ID -> type 1, Odd ID -> type 2
        const kiristetty = workorderId % 3 === 0; // Every third ID is kiristetty

        if (poletype === 1 && kiristetty) return '/assets/icons/powerlines/h_k.png';
        if (poletype === 1 && !kiristetty) return '/assets/icons/powerlines/h.png';
        if (poletype === 2 && kiristetty) return '/assets/icons/powerlines/v_k.png';
        return '/assets/icons/powerlines/v.png';
    }



    createIcon(size: number, color, opacity, border) {
        return L.divIcon({
            className: '',
            iconSize: [size, size], // square size
            html: `<div style="width: 85%; height: 70%; background-color: ${color}; opacity: ${opacity}; border: ${border};"></div>`
        });
    }

    addZoomListener() {
        this.map.on('zoomend', () => {
            const currentZoomLevel = this.map.getZoom();
            const weight = MapUtils.getBorderWeight(currentZoomLevel, 18, 0.3);

            this.workOrderMarkers.forEach((marker: any) => {
                marker.options['size'] = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId);
                marker.setStyle({ weight: weight });
            });

            this.inverterMarkers.forEach((marker: any) => {
                const newSize = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId);
                marker.options['size'] = newSize;                 // keep for consistency with other code
                const scale = (this.customerId === 2) ? 2 : 1;
                const newRadius = (Math.max(newSize[0], newSize[1]) / 5) * scale;
                marker.setRadius(newRadius);                      // circle size follows zoom
                marker.setStyle({ weight: weight });
            });

            this.type3WorkorderMarkers.forEach((marker: any) => {
                marker.options['size'] = MapUtils.getRelativeMarkerSize(marker.options.frame_size, currentZoomLevel, this.customerId);
                marker.setStyle({ weight: weight });
            });

            this.showPowerLineGroups();
        });
    }

    addClickListener() {
        this.map.on('click', (e) => {
            this.resetPolylineHighlight();
        })
    }

    /**
     * Converts a 2022-05-01 datestring to 01.05.2022 datestring.
     * @param datestring
     * @returns
     */
    dashesToDots(datestring) {
        var newdate = datestring.split("-").reverse().join("-")

        newdate = newdate.replace(/\-/g, '.')

        return newdate
    }

    /**
     * Removes "Aloittamatta" from states array.
     * Does not show not started situation
     * @param states all states by definition is shown in UI
     * @returns states array without single state
     */
    filterNotStartedState(states: any[]): any[] {
        return states.filter(s =>
            !(
                s.definition === 'Not started' ||
                s.definition === 'Done' ||
                s.definition === 'Ei aloitettu' ||
                s.definition === 'Valmis' ||
                s.definition === 'Remarks' ||
                s.definition === 'Huomio'
            )
        );
    }

    getOnlyRemarkState(states: any[]): any {
        return states.filter(s => s.definition === 'Remarks' || s.definition === 'Huomio')
    }


    setProgress(amount: number, total: number, givenColor: any = null) {
        let progress;
        if (total > 0) {
            progress = parseFloat((amount / total * 100).toFixed(2))
        } else {
            progress = 0
        }

        let color = "#0058ff"
        // let color = "#FF9F0A"
        // if(progress >= 90 && progress <= 100) {
        //     color = "#10A231"
        // } else if(progress > 100) {
        //     color = "#0058FF"
        // }
        if (givenColor) {
            color = givenColor
        }

        let styles = {
            'width': progress + '%',
            'background-color': color
        };

        return styles
    }

    showDetails(workorderOrInventerId: number): void {
        this.isLoading = true;

        this.allStates.states.forEach(state => state.show = false)
        this.workOrderService.getWorkOrder(workorderOrInventerId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                data => {
                    this.showBaseSidebar = false;
                    this.showSidebar = true;
                    this.selectedMarker = data;
                    this.selectedMarker.imagecount = 0;
                    this.type = data.type;

                    this.selectedStatuses = structuredClone(
                        this.allStates.states.filter(s => s.type === data.type)
                    );

                    if (this.selectedMarker.reports) {
                        for (let report of this.selectedMarker.reports) {
                            for (let status of this.selectedStatuses) {
                                if (status.id == report.status_id) {
                                    if (!status.reports) status.reports = [];
                                    status.reports.push(report);
                                }
                            }
                        }
                    }


                    Object.entries(data.statuses).forEach(([statusKey, status]: [string, any]) => {
                        status.count = status.images.length;
                        this.selectedMarker.imagecount += status.images.length;
                        if (status.images && status.images.length > 0) {
                            forkJoin(
                                status.images.map((image: any) =>
                                    this.workOrderService.fetchImage(image.id).pipe(
                                        map(imageUrl => {
                                            image.url = imageUrl || 'defaultImageUrl'; // Update image URL or set default
                                            return image;
                                        }),
                                        catchError(error => {
                                            console.error('Error fetching image', error);
                                            return of(null);
                                        })
                                    )
                                )
                            ).subscribe(images => {
                                // All images for this status have been fetched and updated
                            });
                        }
                    });
                    this.isLoading = false
                }
            )
    }

    getStatusDefinition() {
        const state = this.allStates.states.find(element => element.id == this.selectedMarker.status_id);
        if (state) {
            return state.definition;
        }
        return null;
    }

    getStatusReports(stateId, selectedMarker) {
        const reports: any[] = []
        if (selectedMarker.reports) {
            for (let i = 0; i < selectedMarker.reports.length; i++) {
                const element = selectedMarker.reports[i]
                if (element.state == stateId) {
                    reports.push(element)
                }
            }
            return reports
        } else return []

    }

    checkLength(array, type) {
        return array.filter(element => element.type === type).length
    }


    openEditReport(report: EditableReport, phaseName: string): void {
        this.editedReport = report;
        this.editedReportPhase = phaseName;
    }

    closeEditReport(): void {
        this.editedReport = null;
        this.editedReportPhase = null;
    }

    onReportChanged(): void {
        if (!this.selectedMarker?.id) return;
        this.showDetails(this.selectedMarker.id);
        this.refreshWorkOrderOnMap(this.selectedMarker.id, this.selectedMarker.type);
    }

    // Refetch from the map list endpoint, its derived fields (color, interrupted, priority_status...) differ from getWorkOrder
    private refreshWorkOrderOnMap(id: number, type: number): void {
        this.workOrderService.getWorkOrders(null, type, [id])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(workOrders => {
                const updated = workOrders?.find(wo => wo.id === id);
                if (updated) this.updateWorkOrderOnMap(updated);
            });
    }

    private updateWorkOrderOnMap(data: WorkOrder): void {
        const layers = {
            [MultiSelectSearchTypes.WORK_ORDER]: { list: this.workOrders, markers: () => this.workOrderMarkers, draw: (ids?: number[]) => this.drawWorkOrders(ids) },
            [MultiSelectSearchTypes.INVERTER]: { list: this.inverters, markers: () => this.inverterMarkers, draw: (ids?: number[]) => this.drawInverters(ids) },
            [MultiSelectSearchTypes.TYPE3]: { list: this.type3Workorders, markers: () => this.type3WorkorderMarkers, draw: (ids?: number[]) => this.drawType3Workorders(ids) },
            [MultiSelectSearchTypes.POWERLINE]: { list: this.powerLineWorkOrders, markers: () => this.powerLineWorkOrderMarkers, draw: (ids?: number[]) => this.drawPowerLineWorkOrders(ids) },
        };
        const layer = layers[data.type];
        const workOrder = layer?.list.find(wo => wo.id === data.id);
        if (!workOrder) return;

        Object.assign(workOrder, data);

        // Not drawn now; updated values are used on next draw
        if (!layer.markers().has(data.id)) return;

        const ids = this.currentFilteredWorkOrders?.type === data.type
            ? this.currentFilteredWorkOrders.workOrders.map(wo => wo.id)
            : undefined;
        this.clearMarkerCollection(layer.markers());
        layer.draw(ids);
    }

    closeSidebar(): void {
        this.closeEditReport();
        this.showSidebar = false;
        this.showBaseSidebar = false;
        this.showRouteSidebar = false;
        this.showRouteNoteSidebar = false;
        this.selectedState = null;
    }

    getCenterCoordinates(): { latitude: number, longitude: number } {
        switch (this.customerId) {
            case 1:
                return {
                    latitude: 60.18208069645202,
                    longitude: 23.97548906518469
                };
            case 2:
                return {
                    latitude: 60.03611032213076,
                    longitude: 18.54047482522265
                };
            case 3: {
                // Prefer powerlines if present; fallback to type3; then frames
                const dynamicCenter = this.getCenterFromWorkOrders(this.powerLineWorkOrders)

                return dynamicCenter ?? { latitude: 60.656023, longitude: 25.279696 };
            }
            case 4:
                return {
                    latitude: 62.1250521303749,
                    longitude: 27.7926603631823
                }
            default:
                return {
                    latitude: 0, // Default latitude
                    longitude: 0 // Default longitude
                };
        }
    }

    private getCenterFromWorkOrders(workOrders: WorkOrder[]): { latitude: number; longitude: number } | null {
        if (!Array.isArray(workOrders) || workOrders.length === 0) return null;

        const points = workOrders
            .map(wo => ({
                lat: Number((wo as any).latitude),
                lng: Number((wo as any).longitude),
            }))
            .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

        if (points.length === 0) return null;

        // center of bounding box (simple + stable)
        let minLat = points[0].lat, maxLat = points[0].lat;
        let minLng = points[0].lng, maxLng = points[0].lng;

        for (const p of points) {
            if (p.lat < minLat) minLat = p.lat;
            if (p.lat > maxLat) maxLat = p.lat;
            if (p.lng < minLng) minLng = p.lng;
            if (p.lng > maxLng) maxLng = p.lng;
        }

        return {
            latitude: (minLat + maxLat) / 2,
            longitude: (minLng + maxLng) / 2,
        };
    }


    setUpCustomRectangleCanvas() {
        L.Canvas.include({
            _updateCustomRectangle: function (layer) {
                const ctx = this._ctx;
                const p = layer._point;
                const size = layer.options.size;
                const halfWidth = size[0] / 2;
                const halfHeight = size[1] / 2;

                ctx.save();
                ctx.beginPath();
                ctx.rect(p.x - halfWidth, p.y - halfHeight, size[0], size[1]);
                ctx.fillStyle = layer.options.fillColor;
                ctx.fill();
                ctx.strokeStyle = layer.options.color;
                ctx.lineWidth = layer.options.weight;
                ctx.stroke();
                ctx.restore();
            }
        });

        L.CustomRectangleMarker = L.CircleMarker.extend({
            options: {
                size: [20, 10], // width, height
            },

            _updatePath: function () {
                this._renderer._updateCustomRectangle(this);
            }
        });

        L.customRectangleMarker = function (latlng, options) {
            return new L.CustomRectangleMarker(latlng, options);
        };
    }


    setUpCustomTextMarkerCanvas() {
        L.Canvas.include({
            _updateCustomText: function (layer) {
                const ctx = this._ctx;
                const p = layer._point;
                const text = layer.options.text;
                const fontSize = layer.options.fontSize || 12;
                const fontColor = layer.options.fontColor || 'black';

                ctx.save();
                ctx.font = `${fontSize}px Arial`;
                ctx.fillStyle = fontColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, p.x, p.y);
                ctx.restore();
            }
        });

        L.CustomTextMarker = L.CircleMarker.extend({
            options: {
                text: '',
                fontSize: 12,
                fontColor: 'black'
            },

            _updatePath: function () {
                this._renderer._updateCustomText(this);
            }
        });

        L.customTextMarker = function (latlng, options) {
            return new L.CustomTextMarker(latlng, options);
        };

    }

    getAmount(state) {
        if (state.type === 3) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 3) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else if (state.type === 2) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 2) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else if (state.type === 1) {
            let amount = 0
            if (this.allStates) {
                this.allStates.states.forEach(element => {
                    if (element.type === 1) {
                        amount += element.workorders.amount
                    }
                });
            }
            return amount
        } else return state.workorders.totaltotal
    }

    getDoneAmount(state) {
        let amount = 0
        let states = this.allStates.states.filter(s => s.type === 3)
        states.forEach(element => {
            if (element.state !== 1 && element.state >= state.state) {
                amount += element.workorders.amount
            }
        });
        return amount
    }

    filterWorkOrders(data: { type: number, workOrders: WorkOrder[] }): void {
        if (!this.map || !data) return;

        // ✅ STORE the current filter
        this.currentFilteredWorkOrders = data;

        const ids = data.workOrders.map(workOrder => workOrder.id);

        // Draw only the filtered markers for the selected type
        // Other types remain unchanged on the map
        switch (data.type) {
            case MultiSelectSearchTypes.WORK_ORDER:
                this.clearMarkerCollection(this.workOrderMarkers);
                this.drawWorkOrders(ids);
                break;

            case MultiSelectSearchTypes.INVERTER:
                this.clearMarkerCollection(this.inverterMarkers);
                this.drawInverters(ids);
                break;

            case MultiSelectSearchTypes.TYPE3:
                this.clearMarkerCollection(this.type3WorkorderMarkers);
                this.drawType3Workorders(ids);
                break;

            case MultiSelectSearchTypes.POWERLINE:
                this.clearMarkerCollection(this.powerLineWorkOrderMarkers);
                this.drawPowerLineWorkOrders(ids);
                break;
        }
    }
    private drawGroup(workOrders: WorkOrder[]): void {
        const ids = workOrders.map(workOrder => workOrder.id).sort();
        const coordinates = workOrders.map(workOrder => {
            return [workOrder.latitude, workOrder.longitude]
        });

        const polylineLayer = L.polyline(coordinates, POWERLINE_GROUP_POLYLINE_SHAPE_OPTIONS)
            .bindPopup(this.getGroupPopUp(ids))
            .on('click', () => {
                this.highlightPolyLine(polylineLayer);
                this.selectedPowerLineGroup = workOrders.slice();
            }).addTo(this.map);

        this.powerLineGroupMarkers.set(ids.join(), { polyline: polylineLayer, workOrders: workOrders });
    }

    private getGroupPopUp(ids: number[]): string {
        return `
        <div class="title">${this.translateService.instant('Poles')}: ${ids?.length}</div>
      `;
    }

    private resetPolylineHighlight(): void {
        if (this.selectedPowerLineGroupHighlight) this.selectedPowerLineGroupHighlight.remove()
    }

    private highlightPolyLine(selectedPolyLine: L.Polyline): void {
        this.resetPolylineHighlight();

        this.selectedPowerLineGroupHighlight = MapUtils.createPolylineHighlight(
            selectedPolyLine,
            POWERLINE_GROUP_HIGHLIGHT_COLOR,
            this.map
        );
    }

    togglePowerLineGroups(): void {
        this.showPowerLineGroupsInput = !this.showPowerLineGroupsInput;
        this.resetPolylineHighlight();
        this.showPowerLineGroups();
    }

    showPowerLineGroups(): void {
        const currentZoom = this.map.getZoom();

        if (this.showPowerLineGroupsInput) {
            if (currentZoom <= 15) {
                this.drawPowerLineGroups();
                this.powerLineGroupsVisible = true;
                return;
            }
        }

        if (this.powerLineGroupsVisible) {
            this.resetPowerLineGroups();
            this.powerLineGroupsVisible = false;
            this.resetPolylineHighlight();
        }
    }

    private resetPowerLineGroups(): void {
        for (let marker of this.powerLineGroupMarkers.values()) {
            marker.polyline?.remove();
        }

        this.drawPowerLineWorkOrders();
    }

    private drawPowerLineGroups(): void {
        for (let marker of this.powerLineGroupMarkers.values()) {
            marker.polyline?.remove();
        }

        for (let marker of this.powerLineWorkOrderMarkers.values()) {
            marker.remove();
        }

        this.powerLineWorkOrders.sort((a, b) => (a.distance || 0) - (b.distance || 0))
        const groups: WorkOrder[][] = [];

        for (let i = 0; i < this.powerLineWorkOrders.length; i += 5) {
            groups.push(this.powerLineWorkOrders.slice(i, i + 5));
        }

        if (groups.length >= 2 && groups[groups.length - 1].length === 1) {
            const lastGroup = groups.pop();
            groups[groups.length - 1] = groups[groups.length - 1].concat(lastGroup!);
        }

        for (let group of groups) {
            this.drawGroup(group);
        }
    }

    private setPowerlineMarkerHighlight(powerline: WorkOrder, selected: boolean): void {
        if (!selected) {
            const marker = this.powerlineHighlightMarkers.get(powerline.id);
            marker?.remove();
            return;
        }

        const marker = this.powerLineWorkOrderMarkers.get(powerline.id);
        if (!marker) return;

        this.removePowerlineMarkerHighlight(powerline);

        const latlng = marker.getLatLng();
        const highlightIcon = L.divIcon({
            className: 'highlight-rectangle',
            iconSize: [34, 34],
            iconAnchor: [17, 17]
        });

        const highlightMarker = L.marker(latlng, {
            icon: highlightIcon,
            interactive: false
        }).addTo(this.map);

        this.powerlineHighlightMarkers.set(powerline.id, highlightMarker);
    }

    private removePowerlineMarkerHighlight(powerline: WorkOrder): void {
        const highlight = this.powerlineHighlightMarkers.get(powerline.id);
        if (highlight) {
            highlight.remove();
            this.powerlineHighlightMarkers.delete(powerline.id);
        }
    }

    private clearMarkerCollection(col: Map<number, L.Marker>) {
        col.forEach(m => m.remove());
        col.clear();
    }

    applyLayerFilter(): void {
        if (!this.map) return;

        // If we have a filter from multi-select-search, use it
        if ((this.hasInverters || this.hasPowerlines) && this.currentFilteredWorkOrders) {
            const ids = this.currentFilteredWorkOrders.workOrders.map(wo => wo.id);

            // Clear all markers
            this.clearMarkerCollection(this.workOrderMarkers);
            this.clearMarkerCollection(this.inverterMarkers);
            this.clearMarkerCollection(this.type3WorkorderMarkers);
            this.clearMarkerCollection(this.powerLineWorkOrderMarkers);

            // Apply layer filter
            switch (this.layerFilter) {
                case MapLayerFilter.ALL:
                    // Draw frames normally (unfiltered)
                    if (this.currentFilteredWorkOrders.type !== MultiSelectSearchTypes.WORK_ORDER) {
                        this.drawWorkOrders();
                    }

                    // Draw the filtered type with filter applied
                    switch (this.currentFilteredWorkOrders.type) {
                        case MultiSelectSearchTypes.WORK_ORDER:
                            this.drawWorkOrders(ids);
                            break;
                        case MultiSelectSearchTypes.INVERTER:
                            this.drawInverters(ids);
                            break;
                        case MultiSelectSearchTypes.TYPE3:
                            this.drawType3Workorders(ids);
                            break;
                        case MultiSelectSearchTypes.POWERLINE:
                            this.drawPowerLineWorkOrders(ids);
                            break;
                    }

                    // Draw other types normally if they're not the filtered type
                    if (this.currentFilteredWorkOrders.type !== MultiSelectSearchTypes.INVERTER) {
                        this.drawInverters();
                    }
                    if (this.currentFilteredWorkOrders.type !== MultiSelectSearchTypes.TYPE3) {
                        this.drawType3Workorders();
                    }
                    if (this.currentFilteredWorkOrders.type !== MultiSelectSearchTypes.POWERLINE && this.customerId === this.customerIds.POWERLINES) {
                        this.drawPowerLineWorkOrders();
                    }
                    break;

                case MapLayerFilter.FRAMES:
                    // Only draw frames (unfiltered)
                    this.drawWorkOrders();
                    break;

                case MapLayerFilter.INVERTERS:
                    // Only draw inverters with filter if that's what's being filtered
                    if (this.currentFilteredWorkOrders.type === MultiSelectSearchTypes.INVERTER) {
                        this.drawInverters(ids);
                    } else {
                        this.drawInverters();
                    }
                    break;
            }
            return;
        }

        // No multi-select filter - draw based on layer filter only
        this.clearMarkerCollection(this.workOrderMarkers);
        this.clearMarkerCollection(this.inverterMarkers);
        this.clearMarkerCollection(this.type3WorkorderMarkers);
        this.clearMarkerCollection(this.powerLineWorkOrderMarkers);

        switch (this.layerFilter) {
            case MapLayerFilter.ALL:
                this.drawWorkOrders();
                this.drawInverters();
                this.drawType3Workorders();
                if (this.customerId === this.customerIds.POWERLINES) this.drawPowerLineWorkOrders();
                break;

            case MapLayerFilter.FRAMES:
                this.drawWorkOrders();
                break;

            case MapLayerFilter.INVERTERS:
                this.drawInverters();
                break;
        }
    }

    onLayerChange(value: MapLayerFilter | string): void {
        this.layerFilter = value as MapLayerFilter;

        // Only clear multi-select-search filter if it exists
        //if (this.hasInverters || this.hasPowerlines) {
        //  this.currentFilteredWorkOrders = null;
        //}

        this.applyLayerFilter();
    }

    formatReportDateTime(dateString: string): string {
        return DateUtils.formatUtcToLocal(dateString, 'datetime');
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next(true)
        this.componentDestroyed$.complete()
        if (this.map) {
            this.map.remove()
            this.map = null
        }
    }
}


